import { yupResolver } from '@hookform/resolvers/yup'
import type { RestRecord } from '@libs/data-access/entities'
import type { RestSchema, ShiftRQSchema } from '@libs/data-access/queries'
import { useCommonAppTranslation } from '@libs/utils/translations'
import RestTypes from '@spa/constants/RestTypes'
import moment from 'moment'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'

import type { MealValue } from '../../../ShiftForm/components/MealRow/MealRow'
import type { PlannedFormData } from '../../models'
import { getBreaksForForm } from '../../utils'
import { useRealHoursSchemaValidations } from '../../validations'

type ShiftRealHoursForm = {
  shift: ShiftRQSchema | undefined
  rest?: RestRecord | RestSchema
  setDidNotShow: boolean
  hasSnapPad: boolean
  breakAsReal: boolean
  padType: string
}

const RestTypesDidNotShow = RestTypes.filter(
  // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
  restType => restType.did_not_show_option
)

export const useRestTypeOptions = () => {
  const { t } = useCommonAppTranslation()

  return (
    RestTypesDidNotShow.valueSeq()
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      .map(restType => ({ value: restType.id, label: t(restType.name) }))
      .toJS()
  )
}

// !!!! NOTE: If you make changes to this file, think about updating useUnplannedShiftRealHoursForm.tsx too

export const useShiftRealHoursForm = ({
  shift,
  rest,
  setDidNotShow,
  hasSnapPad,
  breakAsReal,
  padType
}: ShiftRealHoursForm) => {
  const shiftRealHoursSchemaValidations = useRealHoursSchemaValidations()
  const restTypeOptions = useRestTypeOptions()
  const hasEmployeeModifyReal = Boolean(
    shift?.real_hours_last_modified_by_employee_at
  )
  const momentRealStart = moment(shift?.real_starts_at)
  const momentRealEnd = moment(shift?.real_ends_at)

  const defaultRest = rest?.rest_type
    ? restTypeOptions.find(restOption => restOption.value === rest?.rest_type)
    : restTypeOptions[1]

  const getRealMeals = useMemo(() => {
    if (!shift) {
      return []
    }
    return Number(shift?.shift_meals?.length) > 0
      ? shift?.shift_meals.map(shiftMeal => {
          const mealNotBeenTaken = shiftMeal.real_meal_type === null

          return {
            id: shiftMeal.id || null,
            checked: !mealNotBeenTaken,
            initialMeal: shiftMeal.real_meal_type
              ? shiftMeal.real_meal_type
              : shiftMeal.planned_meal_type,
            meal: {
              value: shiftMeal.real_meal_type
                ? shiftMeal.real_meal_type
                : shiftMeal?.planned_meal_type
            }
          } as MealValue
        })
      : []
  }, [shift])

  const shiftEvents = shift?.shift_events

  const clockInEvent =
    shiftEvents && shiftEvents.find(event => event?.event_type === 'clock_in')
  const clockOutEvent =
    shiftEvents && shiftEvents.find(event => event?.event_type === 'clock_out')

  const shiftIsFinishedWithoutOut =
    !clockOutEvent && moment(shift?.ends_at) < moment(new Date())

  const isShiftUnvalidate = Boolean(shift?.validator_id)

  const defaultValues = {
    breaks: getBreaksForForm(
      shift?.shift_breaks,
      hasSnapPad,
      breakAsReal,
      shiftIsFinishedWithoutOut,
      hasEmployeeModifyReal,
      Boolean(clockInEvent),
      isShiftUnvalidate,
      padType
    ).filter(Boolean) as any,
    meals: getRealMeals,
    label: { value: shift?.label_id || '' },
    witheld_from_pay: shift?.witheld_from_pay,
    shift_time_range: [
      momentRealStart.format('HH:mm'),
      momentRealEnd.format('HH:mm')
    ],
    real_note: shift?.real_note || '',
    did_not_show: setDidNotShow || shift?.did_not_show,
    rest_type: defaultRest,
    note: rest?.note || ''
  }

  const useFormMethods = useForm<PlannedFormData>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(shiftRealHoursSchemaValidations)
  })

  return useFormMethods
}
